/**
 * URL helper functions for handling Azure and local storage URLs
 */

/**
 * Normalizes a media URL to handle both Azure and local storage
 * @param {string} url - The media URL (could be Azure blob URL or local path)
 * @param {string} baseUrl - The base URL for local files (optional)
 * @returns {string} - The normalized URL
 */
export const normalizeMediaUrl = (url, baseUrl = '') => {
  if (!url) return ''
  
  // If it's already a full URL (Azure blob or external), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  
  // If it's a local path, prepend the base URL
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`
  }
  
  // If it's a relative path, prepend base URL and slash
  return `${baseUrl}/${url}`
}

/**
 * Checks if a URL is an Azure blob storage URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's an Azure blob URL
 */
export const isAzureUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  return url.includes('blob.core.windows.net') || url.includes('z21.web.core.windows.net')
}

/**
 * Checks if a URL is a local storage URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a local storage URL
 */
export const isLocalUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  return url.startsWith('/uploads') || url.startsWith('/mock-storage') || 
         (url.startsWith('http://localhost') && (url.includes('/uploads') || url.includes('/mock-storage')))
}

/**
 * Gets the appropriate CDN or optimized URL for media
 * @param {string} url - The original media URL
 * @param {object} options - Options for URL transformation
 * @param {number} options.width - Desired width for image optimization
 * @param {number} options.height - Desired height for image optimization
 * @param {string} options.format - Desired image format (jpeg, png, webp)
 * @param {number} options.quality - Image quality (1-100)
 * @returns {string} - The optimized URL
 */
export const getOptimizedMediaUrl = (url, options = {}) => {
  if (!url) return ''
  
  // For Azure URLs, we could add query parameters for optimization if supported
  if (isAzureUrl(url)) {
    // Azure blob storage can support some query parameters for image processing
    // This would depend on your Azure configuration
    return url
  }
  
  // For local URLs, return as is (could be enhanced with local image processing)
  const getBaseUrl = () => {
    if (import.meta.env.PROD) {
      return '' // Use relative URLs in production
    }
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'
  }
  
  return normalizeMediaUrl(url, getBaseUrl())
}

/**
 * Creates a fallback URL in case the primary URL fails to load
 * @param {string} primaryUrl - The primary URL to try
 * @param {string} fallbackUrl - The fallback URL
 * @returns {object} - Object with primary and fallback URLs
 */
export const createFallbackUrl = (primaryUrl, fallbackUrl = '/images/placeholder.png') => {
  return {
    primary: normalizeMediaUrl(primaryUrl),
    fallback: fallbackUrl
  }
}

/**
 * Validates if a URL is accessible and returns appropriate URL
 * @param {string} url - The URL to validate
 * @returns {Promise<string>} - Promise that resolves to the validated URL
 */
export const validateMediaUrl = async (url) => {
  if (!url) return ''
  
  const normalizedUrl = normalizeMediaUrl(url)
  
  try {
    // For Azure URLs or external URLs, we might want to check if they're accessible
    // For now, just return the normalized URL
    return normalizedUrl
  } catch (error) {
    console.warn('URL validation failed:', error)
    return normalizedUrl
  }
}

/**
 * Extracts filename from a URL (works with both Azure and local URLs)
 * @param {string} url - The URL to extract filename from
 * @returns {string} - The extracted filename
 */
export const extractFilename = (url) => {
  if (!url) return ''
  
  try {
    // Handle both Azure blob URLs and local paths
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    return pathname.split('/').pop() || ''
  } catch (error) {
    // If URL parsing fails, try simple string manipulation
    const parts = url.split('/')
    return parts[parts.length - 1] || ''
  }
}

/**
 * Gets the file extension from a URL
 * @param {string} url - The URL to extract extension from
 * @returns {string} - The file extension (without dot)
 */
export const getFileExtension = (url) => {
  const filename = extractFilename(url)
  const lastDot = filename.lastIndexOf('.')
  return lastDot !== -1 ? filename.substring(lastDot + 1).toLowerCase() : ''
}

/**
 * Checks if a URL points to an image file
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL points to an image
 */
export const isImageUrl = (url) => {
  const extension = getFileExtension(url)
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico']
  return imageExtensions.includes(extension)
}

/**
 * The hosting base domain that client subdomains live on. Mirrors the
 * backend's BASE_DOMAIN (customDomainRouter). Override with VITE_BASE_DOMAIN.
 */
export const SITE_BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'marcoalcini.com'

const slugifyName = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

/**
 * Resolves a site's public host (no protocol), preferring a real domain and
 * falling back to the default subdomain it's served on:
 *   assigned domain → custom domain → Azure URL → {company}.BASE_DOMAIN
 * @param {object} site - site record (may include assigned_domain, custom_domain, company_name, slug, name)
 * @returns {string} host like "demo-company.marcoalcini.com" (or '' if unresolvable)
 */
export const getSiteHost = (site) => {
  if (!site) return ''
  if (site.assigned_domain) return site.assigned_domain
  if (site.custom_domain) return site.custom_domain
  if (site.azure_published_url) {
    return site.azure_published_url.replace(/^https?:\/\//, '').replace(/\/$/, '')
  }
  const slug = slugifyName(site.company_name || site.slug || site.name)
  return slug ? `${slug}.${SITE_BASE_DOMAIN}` : ''
}

/**
 * Same as getSiteHost but as a full https URL (or '' if unresolvable).
 */
export const getSiteUrl = (site) => {
  const host = getSiteHost(site)
  return host ? `https://${host}` : ''
}

export default {
  normalizeMediaUrl,
  isAzureUrl,
  isLocalUrl,
  getOptimizedMediaUrl,
  createFallbackUrl,
  validateMediaUrl,
  extractFilename,
  getFileExtension,
  isImageUrl,
  getSiteHost,
  getSiteUrl,
  SITE_BASE_DOMAIN
}